import { useEffect, useState } from "react";
import { formatDateTime } from "../lib/dateFormat";
import { useNavigate } from "react-router-dom";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import { useAuth, canAtLeast } from "../lib/auth";
import {
  LayoutDashboard,
  Database,
  Boxes,
  ShieldCheck,
  Crown,
  FileText,
  Search,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  ArrowRight,
  Activity,
  Factory,
  ShoppingCart,
  PackageCheck,
  Truck,
  FlaskConical,
  Warehouse,
} from "lucide-react";

const MODULES = [
  { name: "Sales", icon: ShoppingCart, note: "Buyer, consignee, country, grade, variety, brand, price comparison." },
  { name: "Purchase", icon: PackageCheck, note: "Supplier, centre, purchase quality, quantity, value, raw material flow." },
  { name: "QA", icon: FlaskConical, note: "Samples, antibiotic tests, rejected batches, supplier traceability." },
  { name: "Process", icon: Factory, note: "Production, yield, peeling, VA, soaking, IQF/block freeze." },
  { name: "Store", icon: Warehouse, note: "Coldstore stock, availability, dummy cases, material and chemical stock." },
  { name: "PO / Dispatch", icon: Truck, note: "Pending POs, confirmed orders, shipment, on-water, FDA, delivery promptness." },
];

const VALIDATIONS = [
  "Buyer vs consignee mapping confirmation",
  "Sales value, sale price, and payment formulas",
  "Purchase value in lakhs/crores formula",
  "Confirmed order reverse raw material calculation",
  "Batch-wise QA test history without overwrite",
  "On-time, early, delayed, on-water shipment rules",
];

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [dashboards, setDashboards] = useState([]);
  const [connections, setConnections] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [reports, setReports] = useState([]);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/dashboards").then((r) => setDashboards(r.data.dashboards || [])).catch(() => {});
    api.get("/connections").then((r) => setConnections(r.data.connections || [])).catch(() => {});
    api.get("/datasets").then((r) => setDatasets([...(r.data.datasets || []), ...(r.data.demo_datasets || [])])).catch(() => {});
    api.get("/reports").then((r) => setReports(r.data.reports || [])).catch(() => {});

    if (canAtLeast(user, "workspace_admin")) {
      api.get("/audit-logs?limit=6").then((r) => setAudit(r.data.logs || [])).catch(() => {});
    }
  }, [user]);

  const liveConnections = connections.filter((c) => c.status === "ok").length;

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>Camaroo Analytics Super Admin</h1>
            <p>
              Clean V2 control panel for rebuilding the CEO Command Center, reports, datasets,
              role-based dashboards, and client-ready analytics flow.
            </p>
          </div>

          <button className="erp-primary-btn" onClick={() => nav("/ceo-command-center")}>
            <Crown size={15} strokeWidth={1.8} />
            Open CEO Command Center
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
          <StatCard label="Active Dashboards" value={dashboards.length} note="Available dashboards" icon={LayoutDashboard} />
          <StatCard label="Live Connections" value={`${liveConnections}/${connections.length}`} note="Connected data sources" icon={Database} />
          <StatCard label="Datasets" value={datasets.length} note="Analytics datasets" icon={Boxes} />
          <StatCard label="Reports" value={reports.length} note="Available reports" icon={FileText} />
        </div>

        <div className="grid grid-cols-1 gap-4 mb-5">
          <div className="erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>Business Modules</h2>
                <p>Access operational analytics across the core ERP business functions.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {MODULES.map((m) => {
                const Icon = m.icon;
                const tabByModule = {
                  Sales: "buyer",
                  Purchase: "purchase",
                  QA: "qc",
                  Process: "production",
                  Store: "store",
                  "PO / Dispatch": "po",
                };
                const tab = tabByModule[m.name];

                return (
                  <button
                    key={m.name}
                    type="button"
                    className="erp-module-card text-left w-full cursor-pointer"
                    onClick={() => nav(`/ceo-command-center?tab=${tab}`)}
                    aria-label={`Open ${m.name} analytics`}
                  >
                    <div className="erp-module-icon">
                      <Icon size={18} strokeWidth={1.8} />
                    </div>
                    <div>
                      <div className="erp-module-title">{m.name}</div>
                      <div className="erp-module-note">{m.note}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>Quick Actions</h2>
                <p>Most-used admin actions for the rebuild.</p>
              </div>
            </div>

            <div className="erp-action-grid">
              <ActionButton icon={Crown} label="CEO Command Center" onClick={() => nav("/ceo-command-center")} />
              <ActionButton icon={Database} label="Data Sources" onClick={() => nav("/data-sources")} />
              <ActionButton icon={Boxes} label="Datasets" onClick={() => nav("/datasets")} />
              <ActionButton icon={Search} label="SQL Editor" onClick={() => nav("/sql")} />
              <ActionButton icon={LayoutDashboard} label="New Dashboard" onClick={() => nav("/builder/new")} />
              <ActionButton icon={ShieldCheck} label="Access Management" onClick={() => nav("/access-management")} />
            </div>
          </div>

          <div className="erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>Recent Activity</h2>
                <p>Latest platform actions.</p>
              </div>
            </div>

            <div className="erp-activity-list">
              {audit.length === 0 && (
                <div className="erp-empty-state">No recent activity available.</div>
              )}

              {audit.map((a) => (
                <div key={a.id} className="erp-activity-row">
                  <div>
                    <strong>{a.action}</strong>
                    <span>{a.user_email || "System"}</span>
                  </div>
                  <small>{formatDateTime(a.created_at)}</small>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, note, icon: Icon }) {
  return (
    <div className="erp-stat-card">
      <div>
        <span>{label}</span>
        <strong>{value ?? "—"}</strong>
        <small>{note}</small>
      </div>
      <div className="erp-stat-icon">
        <Icon size={19} strokeWidth={1.8} />
      </div>
    </div>
  );
}

function StatusRow({ icon: Icon, label, value, good, danger }) {
  return (
    <div className={`erp-status-row ${good ? "good" : ""} ${danger ? "danger" : ""}`}>
      <Icon size={15} strokeWidth={1.8} />
      <div>
        <strong>{label}</strong>
        <span>{value}</span>
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick }) {
  return (
    <button type="button" className="erp-action-btn" onClick={onClick}>
      <Icon size={15} strokeWidth={1.8} />
      <span>{label}</span>
      <ArrowRight size={13} strokeWidth={1.8} />
    </button>
  );
}
